import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BadgeComponent,
  ButtonDirective,
  CalloutComponent,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  CollapseDirective,
  OffcanvasModule,
  SpinnerComponent
} from '@coreui/angular-pro';
import { VehicleStatusService } from '../../../../services/vehicle-status.service';
import {
  IEventExceptionReasons,
  IUtilisationEvent,
  IUtilisationEventMetadata,
  IUtilisationEventResponse,
  IUtilisationSnapshotResponse,
  IVehicleUtilisationSnapshot,
  IUtilizationVOREvent,
} from '../../../../models/vehicle-status.model';
import { lastValueFrom, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { cilListNumbered, cilPaperPlane, brandSet } from '@coreui/icons';
import {
  AccordionComponent,
  AccordionItemComponent,
  AccordionButtonDirective,
  TemplateIdDirective,
} from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { PermissionsProvider } from '../../../../services/permission.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-status-flyout',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    CommonModule,
    OffcanvasModule,
    CollapseDirective,
    ButtonDirective,
    IconDirective,
    CardComponent, CardBodyComponent, CardHeaderComponent,
    BadgeComponent,
    CalloutComponent,
    SpinnerComponent,
    AccordionComponent,
    AccordionItemComponent,
    AccordionButtonDirective,
    TemplateIdDirective,
  ],
  templateUrl: './status-flyout.component.html',
  styleUrl: './status-flyout.component.scss'
})
export class StatusFlyoutComponent implements OnInit {
  @Input({ required: true }) visible!: boolean;
  @Output() visibleChange = new EventEmitter<boolean>();
  visibleChangeSubscription!: Subscription;

  @Input({ required: true }) snapshot: IVehicleUtilisationSnapshot = {} as IVehicleUtilisationSnapshot;
  
  manualEventAddPanelShow = false;
  vehicleUtilizationEvents!: IUtilisationEvent[];
  vehicleUtilizationLastEventMeta: IUtilisationEventMetadata | null = null; 
  vehicleUtilizationVorEvents: IUtilizationVOREvent[] = [];

  status: number = -1;
  notes: string = '';
  reasonsList: IEventExceptionReasons[] = [];
  isLoading = false;

  constructor(
    private vehicleStatusService: VehicleStatusService,
    public permissions: PermissionsProvider,
    private cdr: ChangeDetectorRef
  ) {
   }

  ngOnInit() {
    this.getEventsReasonList();
  }

  getUtilisationChangeType(type: number) {
    return this.vehicleStatusService.getUtilisationChangeTypeName(type)
  }
  getReferenceObjectType(type: string) {
    return this.vehicleStatusService.getReferenceObjectTypeName(type)
  }


  getUtilisationBadgeColour(utilisationStatus: boolean, vor: boolean) {
    return this.vehicleStatusService.getUtilisationBadgeColour(utilisationStatus, vor)
  }

  getUtilisationStatus(snapshot: IVehicleUtilisationSnapshot) {
    return this.vehicleStatusService.getUtilisationStatus(snapshot);
  }


  async onUpdateStatus() {
    if (this.status !== -1 && this.notes !== '' && this.snapshot !== undefined) {
      this.isLoading = true;
      const added$ = this.vehicleStatusService.addUtilizationEvent(this.snapshot.id, this.status, this.notes)
      const postEventResponse = await lastValueFrom(added$);
      this.isLoading = false;
      console.log('onUpdateStatus: postEventResponse: ', postEventResponse)
     
      if (postEventResponse) {
        const lastEvent = postEventResponse;
        console.log('postEventResponse.valueOf(): ', lastEvent);
       
        this.getEventsList();
        if (lastEvent.utilization_change_type != 2) {
          this.snapshot.is_utilized = lastEvent.utilization_change_type === 0 ? true : false;
        }
        // Reset the form
        this.status = -1;
        this.notes = '';

        this.visibleChange.emit(true);
      }
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshot'] && this.snapshot?.id) {
      this.getEventsList();
    }
  }

  private getEventsReasonList() {
    this.reasonsList = [];
    this.vehicleStatusService.getUtilizationEventReasons().subscribe((data) => {
      this.reasonsList = data;
      this.cdr.detectChanges();
    })
  }

  async loadVorEventDetails (activity: IUtilizationVOREvent) {
    if (activity.details !== null) {
      // Skipping if the activity details are already loaded
      return
    } else if (!Number.isFinite(Number(activity.activity_id))) {
      // Skipping if the Activity's ID isn't a valid number (to handle older utilization events)
      activity.details = {
        description: 'Unable to load',
        status_display: '',
        record_id: '',
        status: '',
      }
      this.cdr.detectChanges()
      return
    }
    switch (activity.activity_class) {
      case "VehicleActivity":
        const vehicleActivity = await firstValueFrom(
        this.vehicleStatusService.getVehicleActivityById(activity.activity_id))
        console.log({ vehicleActivity })
        activity.details = {
          description: vehicleActivity.description,
          status_display: vehicleActivity.trigger_status_name,
          record_id: vehicleActivity.record_id,
          status: vehicleActivity.status,
        }
        break
      case "ScheduledActivity":
        const scheduledActivity = await firstValueFrom(
          this.vehicleStatusService.getScheduledActivityById(activity.activity_id))
        console.log({ scheduledActivity })
        activity.details = {
          description: scheduledActivity.schedule_type_name,
          status_display: scheduledActivity.status_display,
          record_id: scheduledActivity.id.toString(),
          status: scheduledActivity.status,
        }
        break
    }
    this.cdr.detectChanges();
  }

  private async getEventsList() {
    this.isLoading = true;
    this.vehicleUtilizationVorEvents = [];
    this.vehicleUtilizationLastEventMeta = null;
    this.vehicleStatusService.getVehicleUtilizationEvents(this.snapshot.id).subscribe((data) => {
      this.isLoading = false;
      const _data = data.results;
      this.vehicleUtilizationEvents = _data;
      if (_data.length > 0) {
        const lastEvent = _data[_data.length - 1]
        const metadata = lastEvent.metadata;
        console.log({ metadata })
        this.vehicleUtilizationLastEventMeta = Object.keys(metadata).length > 0 ? metadata : null;
        this.vehicleUtilizationEvents = _data.reverse();
        this.vehicleUtilizationVorEvents = this.vehicleUtilizationLastEventMeta?._current_vor_activities
          .split(', ').map(i => {
            const vorEventFields = i.split(' ')
            return {
              activity_class: vorEventFields[0].replace("[", "").replace("]", ""),
              activity_type: vorEventFields[1],
              activity_id: vorEventFields[2].replace("#", ""),
              details: null,
            }
          }) || []
      }
     
      this.cdr.detectChanges();
    });
  }

  toggleCollapse(): void {
    this.manualEventAddPanelShow = !this.manualEventAddPanelShow;
    this.vehicleUtilizationVorEvents = []
  }

}
